/**
 * A list row that BEHAVES like a button without being one.
 *
 * Browsers refuse to select text inside a `<button>` — and not through a
 * stylesheet rule you can override: `user-select: text` on the button changes
 * the computed value and still selects nothing. That is the right default for
 * a control whose label is chrome, and the wrong one for the rows that list
 * what people actually wrote (a comment snippet, a mention excerpt), because
 * those rows ARE the text somebody wants to copy out of Kola.
 *
 * So the row is a div carrying the button role, with the two things the real
 * element gave for free put back by hand:
 *
 *  - keyboard activation (Enter / Space, the button contract), and
 *  - one guard: a click that ENDED a text selection was a highlight, not a
 *    press. A plain click collapses the selection before `click` fires, so
 *    this only ever suppresses the tail of a drag — the same test the script
 *    view uses for its own click-through (ScriptView.tsx).
 */
import type { KeyboardEvent, ReactNode } from "react";

export interface RowButtonProps {
  className: string;
  title?: string;
  onActivate(): void;
  children: ReactNode;
}

export function RowButton({ className, title, onActivate, children }: RowButtonProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onActivate();
  };
  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      title={title}
      onClick={() => {
        if ((window.getSelection()?.toString() ?? "") !== "") return;
        onActivate();
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}
